import { useCallback, useEffect, useState } from 'react';

import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ChevronLeft, ChevronRight, EyeIcon } from 'lucide-react';

import {
  ANIMATIONS,
  DIMENSIONS,
} from '@modules/_shared/components/report-editor/constants';
import MobileStepperHeader from '@modules/_shared/components/report-editor/MobileStepperHeader';
import { REPORT_ENABLE_SECTION_ANCHOR } from '@modules/_shared/components/report-editor/preview/previewAnchors';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import ReportEditorStepperMenu from '@modules/_shared/components/report-editor/ReportEditorStepperMenu';
import { useLocale } from '@shared/hooks/locale';
import { cssTokens, customPalette } from '@shared/theme/StyleProvider';

const ReportEditorStepperComponent = () => {
  const { breakpoints } = useTheme();
  const { t } = useLocale();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const isTablet = useMediaQuery(breakpoints.down('md'));
  const isDesktop = useMediaQuery(breakpoints.up('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {
    steps,
    currentStepIndex,
    currentStepData,
    setCurrentStepByIndex,
    data,
    isContentPanelExpanded,
    setContentPanelExpanded,
    toggleStepVisibilityRef,
  } = useReportContext();

  const handlePreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepByIndex(currentStepIndex - 1);
    }
  }, [currentStepIndex, setCurrentStepByIndex]);

  const handleNextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepByIndex(currentStepIndex + 1);
    }
  }, [currentStepIndex, setCurrentStepByIndex, steps.length]);

  const handleMenuClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleStepSelect = useCallback(
    (index: number) => {
      setCurrentStepByIndex(index);
      handleMenuClose();
    },
    [handleMenuClose, setCurrentStepByIndex],
  );

  useEffect(() => {
    if (isTablet && !isContentPanelExpanded) {
      setContentPanelExpanded(true);
    }
  }, [isTablet, isContentPanelExpanded, setContentPanelExpanded]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        height: '100%',
        width: isMobile
          ? '100%'
          : isContentPanelExpanded
          ? `${DIMENSIONS.CONTENT_PANEL_WIDTH}px`
          : '0px',
        overflow: 'hidden',
        position: 'relative',
        transition: `width ${ANIMATIONS.TRANSITION_DURATION} ${ANIMATIONS.TRANSITION_TIMING}`,
        backgroundColor: customPalette.superLightBlue,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isMobile ? '100%' : `${DIMENSIONS.CONTENT_PANEL_WIDTH}px`,
          minWidth: isMobile ? 'auto' : `${DIMENSIONS.CONTENT_PANEL_WIDTH}px`,
          height: '100%',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            justifyContent: 'space-between',
            height: `${DIMENSIONS.HEADER_HEIGHT}px`,
          }}
        >
          {isMobile ? (
            <MobileStepperHeader
              handleMenuClick={handleMenuClick}
              onPreviousStep={handlePreviousStep}
              onNextStep={handleNextStep}
            />
          ) : (
            <>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {currentStepData.title}
              </Typography>
              {isDesktop && (
                <IconButton
                  onClick={() =>
                    setContentPanelExpanded(!isContentPanelExpanded)
                  }
                  sx={{
                    ml: 'auto',
                  }}
                >
                  {isContentPanelExpanded ? (
                    <ChevronLeft size={20} />
                  ) : (
                    <ChevronRight size={20} />
                  )}
                </IconButton>
              )}
            </>
          )}
        </Box>

        <ReportEditorStepperMenu
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          handleStepSelect={handleStepSelect}
        />

        {currentStepData.Component && (
          <Box
            data-step-container
            sx={{
              flexGrow: 1,
              position: 'relative',
              overflowY: 'auto',
              height: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {'active' in currentStepData &&
              currentStepData.active === false && (
                <>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: cssTokens.backdrop,
                      zIndex: 9,
                      height: isMobile
                        ? 'calc(100dvh - 52px)'
                        : 'calc(100% - 52px)',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 10,
                      height: isMobile
                        ? 'calc(100dvh - 52px)'
                        : 'calc(100% - 52px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 2,
                      pointerEvents: 'none',
                    }}
                  >
                    <Button
                      data-tour={REPORT_ENABLE_SECTION_ANCHOR}
                      variant="contained"
                      startIcon={<EyeIcon />}
                      onClick={() => {
                        if (toggleStepVisibilityRef.current) {
                          toggleStepVisibilityRef.current(currentStepIndex);
                        }
                      }}
                      sx={{ pointerEvents: 'auto' }}
                    >
                      {t('Enable this section')}
                    </Button>
                  </Box>
                </>
              )}

            <currentStepData.Component
              reportId={data.id}
              setStep={setCurrentStepByIndex}
              step={currentStepIndex}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReportEditorStepperComponent;
